import { Link, useParams } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { PublicNavbar } from "@/components/public-navbar";
import { SeoHead } from "@/components/seo-head";
import { NotFoundPage } from "@/pages/layout";
import { POSTS, getPost } from "@/pages/blog/posts";

function formatDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function BlogIndexPage() {
  return (
    <div className="min-h-screen">
      <SeoHead
        title="Blog"
        description="Guides and updates from the TodoLLM team on AI agents, MCP, and task tracking."
        canonical="https://todollm.com/blog"
      />
      <PublicNavbar />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <h1 className="text-3xl font-semibold tracking-tight">Blog</h1>
        <ul className="mt-8 divide-y divide-border border-t border-border">
          {POSTS.map((post) => (
            <li key={post.slug} className="py-6">
              <Link
                to="/blog/$slug"
                params={{ slug: post.slug }}
                className="group block"
              >
                <h2 className="text-xl font-medium transition-colors group-hover:text-primary">
                  {post.title}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {[post.author, formatDate(post.created_at)]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams({ strict: false }) as { slug?: string };
  const post = getPost(slug);

  if (!post) return <NotFoundPage />;

  return (
    <div className="min-h-screen">
      <SeoHead
        title={post.title}
        description={`${post.title} — by ${post.author}`}
        canonical={`https://todollm.com/blog/${post.slug}`}
        ogType="article"
      />
      <PublicNavbar />
      <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <header className="mb-10 border-b border-border pb-8">
          <h1 className="text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
            {post.title}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {[post.author, formatDate(post.created_at)]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </header>
        <div className="prose prose-invert prose-themed prose-sm max-w-none sm:prose-base prose-headings:scroll-mt-24 prose-headings:font-semibold prose-h2:mt-12 prose-h2:text-2xl prose-a:font-medium prose-a:no-underline hover:prose-a:underline">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {post.content}
          </ReactMarkdown>
        </div>
      </article>
    </div>
  );
}
